import { type LeadContact } from '@/models/salesLead'

export interface LeadEmail {
  label: string
  value: string
  // When the address belongs to a specific contact (e.g. a carrier contact),
  // the composer targets that contact. Absent for plain lead emails.
  contactId?: string
}

const EMAIL_LABEL = 'Email'

/**
 * Reachable email addresses for a single lead contact, as dropdown entries:
 * the primary email followed by each secondary email. Addresses are trimmed,
 * blanks dropped, and deduped case-insensitively (keeping the first seen).
 */
export function contactToEmails(contact: LeadContact | null | undefined): LeadEmail[] {
  if (!contact) return []
  const emails: LeadEmail[] = []
  const seen = new Set<string>()
  const push = (raw: string | null | undefined) => {
    const value = (raw ?? '').trim()
    if (!value) return
    const key = value.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    emails.push({ label: EMAIL_LABEL, value })
  }
  push(contact.primaryEmail)
  // Guard against partial payloads (deploy skew) where secondaryEmails is absent
  // despite the type — iterating undefined here would crash the whole Activity tab.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  for (const raw of contact.secondaryEmails ?? []) push(raw)
  return emails
}

/**
 * All reachable emails across a lead's contacts (primary contact first, as the
 * API orders them), deduped by value. With a single contact the Email label is
 * kept; with multiple contacts each email is labeled by its contact's name
 * (falling back to Email) so the dropdown shows who.
 */
export function leadContactEmails(contacts: LeadContact[]): LeadEmail[] {
  const multi = contacts.length > 1
  const emails: LeadEmail[] = []
  const seen = new Set<string>()
  for (const contact of contacts) {
    for (const e of contactToEmails(contact)) {
      const key = e.value.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      emails.push(multi ? { label: contact.name || e.label, value: e.value } : e)
    }
  }
  return emails
}
